import CircleWarningIcon from '@assets/icons/circle-warning.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect } from 'react';
import { AgentTaskCard } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-task-card';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { Button, ButtonVariant } from '@sales-portal/components/button';
import { createStyles } from '@/contexts/theme-context';
import { EmptyState } from '@experiences/components/empty-state';
import {
  localActionCountsForType,
  useRecommendationActionsStore,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import { PricingTableSkeleton } from '@sales-portal/domain/pricing/screens/management/components/pricing-table-skeleton';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBusinessOrganizationPricingConfig } from '@/experiences/sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useNavigation } from '@react-navigation/native';
import { usePricingTaskRunsByTypeLoader } from '@/experiences/sales-portal/domain/pricing-agent/blocs/loaders/use-pricing-task-runs-by-type-loader';
import { useTaskRunGroupsTransformer } from '@/experiences/sales-portal/domain/pricing-agent/blocs/transformers/use-task-run-groups-transformer';
import { View } from 'react-native';

export const PricingAgentTasksScreen: React.FC = () => {
  const [styles, theme] = useStyles();
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();
  const { currencyCode, hasPricingAgentAccess } =
    useBusinessOrganizationPricingConfig();

  const { taskRuns, error, loading, refetch } = usePricingTaskRunsByTypeLoader({
    hasPricingAgentAccess,
  });
  const groups = useTaskRunGroupsTransformer(taskRuns);

  const pruneStaleTaskTypes = useRecommendationActionsStore(
    (state) => state.pruneStaleTaskTypes,
  );
  const activeTaskTypeId = useRecommendationActionsStore(
    (state) => state.activeTaskTypeId,
  );
  const activeActions = useRecommendationActionsStore((state) => state.actions);
  const slicesByType = useRecommendationActionsStore(
    (state) => state.slicesByType,
  );

  useEffect(() => {
    if (loading || error) {
      return;
    }
    const validTaskTypeIds = taskRuns
      .map((taskRun) => taskRun.task_type_id)
      .filter((id): id is string => !!id);
    pruneStaleTaskTypes(validTaskTypeIds);
  }, [error, loading, pruneStaleTaskTypes, taskRuns]);

  const onCardPress = useCallback(
    (
      taskTypeUUID: string,
      taskName: string,
      lastCompletedAt: string | null,
    ) => {
      navigation.navigate(SALES_PORTAL_ROUTES.PricingAgentReview, {
        lastCompletedAt: lastCompletedAt ?? undefined,
        taskName,
        taskTypeUUID,
      });
    },
    [navigation],
  );

  const hasTasks = groups.length > 0;

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        leadingActions={
          <PricingBreadcrumbs
            secondaryBreadcrumb={i18n.t('pricing.agent.sidenav_title')}
          />
        }
        scroll={hasTasks}
      >
        {loading ? (
          <View style={styles.loadingContent}>
            <PricingTableSkeleton />
          </View>
        ) : error ? (
          <EmptyState
            bottomElement={
              <Button
                accessibilityLabel={i18n.t('sales_portal.error.retry')}
                onPress={() => refetch()}
                variant={ButtonVariant.Secondary}
                width={120}
              >
                {i18n.t('sales_portal.error.retry')}
              </Button>
            }
            description={i18n.t('pricing.error.table.description')}
            iconElement={
              <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
            }
            title={i18n.t('pricing.error.table.title')}
          />
        ) : hasTasks ? (
          <View style={styles.content}>
            <Typography variant="heading-lg">
              {i18n.t('pricing.agent.title')}
            </Typography>
            {groups.map((group) => (
              <View key={group.taskGroup} style={styles.group}>
                <Typography
                  color={theme.sk.color.text.tertiary}
                  variant="paragraph-md"
                >
                  {group.taskGroup}
                </Typography>
                {group.cards.map((card) => (
                  <AgentTaskCard
                    card={card}
                    currencyCode={currencyCode}
                    key={card.taskTypeId}
                    localActionCounts={localActionCountsForType(
                      {
                        actions: activeActions,
                        activeTaskTypeId,
                        slicesByType,
                      },
                      card.taskTypeId,
                    )}
                    onPress={() =>
                      onCardPress(
                        card.taskTypeId,
                        card.name,
                        card.lastCompletedAt,
                      )
                    }
                  />
                ))}
              </View>
            ))}
          </View>
        ) : (
          <EmptyState
            description={i18n.t(
              'pricing.agent.review.no_task_runs.description',
            )}
            iconElement={
              <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
            }
            title={i18n.t('pricing.agent.review.no_task_runs.title')}
          />
        )}
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  content: {
    gap: theme.sk.spacing.spaceLarge,
    paddingBottom: theme.sk.spacing.spaceLarge,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  group: {
    gap: theme.sk.spacing.spaceMedium,
  },
  loadingContent: {
    flex: 1,
    gap: 42,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
}));
